import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileText,
  RefreshCcw,
  X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { disputeIntelligenceService } from '../../services/disputeIntelligenceService';
import ExistingDocumentPicker from '../documents/ExistingDocumentPicker';
import { resolvePreparedDocumentUrl } from '../../utils/supportingDocumentApi';
import {
  loadInvestigationResultsState,
  markInvestigationResultsAttached,
  markInvestigationResultsDetached,
  normalizeInvestigationSubmission,
  resolveInvestigationSubmission,
} from '../../utils/investigationResultsPersistence';

function BureauRow({ bureau, t, ns }) {
  const waiting = bureau.waiting_period || {};
  const isReady = bureau.ready_for_next_round;
  const isWaiting = bureau.state === 'waiting';

  return (
    <div
      className="rounded-xl border border-stone-200 dark:border-emerald-800/30 p-4 bg-white/70 dark:bg-[#0a1812]/50"
      data-testid={`eligibility-${bureau.bureau_key}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h4 className="font-semibold text-stone-900 dark:text-white">{bureau.bureau}</h4>
        <span
          className={`text-xs font-semibold px-2 py-1 rounded-full ${
            isReady
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
              : isWaiting
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                : 'bg-stone-100 text-stone-600 dark:bg-stone-800/50 dark:text-stone-400'
          }`}
        >
          {bureau.status_label}
        </span>
      </div>

      {isWaiting && waiting.waiting_days && (
        <div className="text-sm text-stone-600 dark:text-emerald-100/80 space-y-1">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-amber-500" />
            <span>{t(`${ns}.waitingPeriod`)}</span>
          </div>
          <p>
            {t(`${ns}.daysCompleted`, {
              completed: String(waiting.days_completed || 0),
              total: String(waiting.waiting_days || 30),
            })}
          </p>
          <p className="font-medium text-amber-700 dark:text-amber-300">
            {t(`${ns}.daysRemaining`, { days: String(waiting.days_remaining || 0) })}
          </p>
        </div>
      )}

      {isReady && (
        <p className="text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
          <CheckCircle size={14} />
          {t(`${ns}.readyForNextRound`)}
        </p>
      )}
    </div>
  );
}

function InvestigationResultsCard({
  attachment,
  onChange,
  onRemove,
  removing,
  t,
  ns,
}) {
  const previewUrl = attachment.thumbnail_url || attachment.file_url;
  const isFromLibrary = attachment.is_existing_document_reference || attachment.document_id;

  return (
    <div
      className="mb-4 p-3 rounded-lg bg-emerald-50 dark:bg-[rgba(16,185,129,0.10)] border border-emerald-200 dark:border-[rgba(16,185,129,0.25)]"
      data-testid="investigation-results-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-lg overflow-hidden bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] flex-shrink-0 border border-emerald-200 dark:border-[rgba(16,185,129,0.30)]">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={attachment.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <FileText size={20} className="text-emerald-600 dark:text-emerald-400" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
            {t(`${ns}.attachmentLabel`)}
            {isFromLibrary && (
              <span className="text-xs text-blue-600 dark:text-blue-400 ml-2 font-normal">
                — {t(`${ns}.fromMyDocuments`)}
              </span>
            )}
          </p>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 truncate">
            {attachment.title || attachment.filename}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Badge className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] text-emerald-700 dark:text-emerald-400 text-xs">
            {t(`${ns}.attachedBadge`)}
          </Badge>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={onChange}
              disabled={removing}
              className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-[rgba(59,130,246,0.15)] text-stone-500 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50"
              title={t(`${ns}.changeDocument`)}
              data-testid="change-investigation-results-btn"
            >
              <RefreshCcw size={14} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={removing}
              className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-[rgba(239,68,68,0.15)] text-stone-400 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-50"
              title={t(`${ns}.removeDocument`)}
              data-testid="remove-investigation-results-btn"
            >
              {removing ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RoundEligibilityPanel({ token, t, ns = 'roundEligibility' }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.user_id || user?.id || user?.email || 'anonymous';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [showExistingPicker, setShowExistingPicker] = useState(false);

  const fileInputRef = useRef(null);
  const replaceModeRef = useRef(false);

  const pickDefaultBureau = useCallback((eligibility) => {
    const ready = eligibility?.bureaus?.find((b) => b.ready_for_next_round);
    if (ready?.bureau_key) return ready.bureau_key;
    const withUpload = eligibility?.bureaus?.find((b) => b.has_investigation_upload);
    if (withUpload?.bureau_key) return withUpload.bureau_key;
    return 'experian';
  }, []);

  const applyEligibility = useCallback(
    (eligibility) => {
      setData(eligibility);
      const apiSubmission = normalizeInvestigationSubmission(
        eligibility?.active_investigation_submission
      );
      const localState = loadInvestigationResultsState(userId);
      const resolved = resolveInvestigationSubmission(apiSubmission, localState);
      setAttachment(resolved);
      if (resolved) {
        markInvestigationResultsAttached(userId, resolved);
      }
    },
    [userId]
  );

  const refreshEligibility = useCallback(async () => {
    if (!token) return null;
    const refreshed = await disputeIntelligenceService.getRoundEligibility(token);
    applyEligibility(refreshed);
    return refreshed;
  }, [applyEligibility, token]);

  useEffect(() => {
    if (!token) return;
    disputeIntelligenceService
      .getRoundEligibility(token)
      .then(applyEligibility)
      .catch(() => {
        const localState = loadInvestigationResultsState(userId);
        if (localState?.submission && !localState.detached) {
          setAttachment(normalizeInvestigationSubmission(localState.submission));
        }
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [applyEligibility, token, userId]);

  const submitFileUpload = async (file, bureau) => {
    if (!file || !token) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('bureau', bureau);
      fd.append('investigation_completed', 'true');
      fd.append('file', file);
      const result = await disputeIntelligenceService.uploadInvestigationResults(fd, token);
      const submission = normalizeInvestigationSubmission(result.submission) || {
        submission_id: `local_${Date.now()}`,
        bureau,
        filename: file.name,
        title: file.name,
        source: 'upload',
        investigation_completed: true,
        uploaded_at: new Date().toISOString(),
      };
      setAttachment(submission);
      markInvestigationResultsAttached(userId, submission);
      await refreshEligibility();
    } finally {
      setUploading(false);
      replaceModeRef.current = false;
    }
  };

  const submitExistingDocument = async (docAttachment) => {
    if (!docAttachment?.document_id || !token) return;
    setUploading(true);
    try {
      const bureau = pickDefaultBureau(data);
      const result = await disputeIntelligenceService.attachInvestigationDocument(
        {
          bureau,
          documentId: docAttachment.document_id,
          investigationCompleted: true,
        },
        token
      );
      const submission =
        normalizeInvestigationSubmission(result.submission) ||
        normalizeInvestigationSubmission({
          ...docAttachment,
          submission_id: docAttachment.document_id,
          bureau,
          filename: docAttachment.title,
          source: 'existing_document',
          uploaded_at: new Date().toISOString(),
          thumbnail_url: docAttachment.thumbnail_url || resolvePreparedDocumentUrl(docAttachment),
          file_url: docAttachment.file_url || resolvePreparedDocumentUrl(docAttachment),
        });
      setAttachment(submission);
      markInvestigationResultsAttached(userId, submission);
      await refreshEligibility();
    } finally {
      setUploading(false);
      replaceModeRef.current = false;
      setShowExistingPicker(false);
    }
  };

  const openPickerFlow = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleUploadShortcut = () => {
    replaceModeRef.current = Boolean(attachment);
    openPickerFlow();
  };

  const handleFileInputChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const bureau = pickDefaultBureau(data);
    await submitFileUpload(file, bureau);
  };

  const handleRemoveAttachment = async () => {
    if (!attachment || removing) return;
    const submissionId = attachment.submission_id;
    setRemoving(true);
    setAttachment(null);
    markInvestigationResultsDetached(userId, submissionId);
    try {
      if (token) {
        await disputeIntelligenceService.detachInvestigationResults(submissionId, token);
        await refreshEligibility();
      }
    } catch {
      /* local detach already applied */
    } finally {
      setRemoving(false);
    }
  };

  const handleChangeAttachment = () => {
    replaceModeRef.current = true;
    setShowExistingPicker(true);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-stone-500 dark:text-emerald-300/70 py-4">
        <Loader2 size={16} className="animate-spin" />
        {t(`${ns}.loading`)}
      </div>
    );
  }

  if (!data?.bureaus?.length) return null;

  return (
    <div
      className="rounded-2xl border border-stone-200 dark:border-emerald-800/30 p-5 bg-gradient-to-br from-white to-emerald-50/20 dark:from-[#0d2018] dark:to-[#0d2018]"
      data-testid="round-eligibility-panel"
    >
      <h3 className="text-lg font-bold text-stone-900 dark:text-white mb-1">{t(`${ns}.title`)}</h3>
      <p className="text-sm text-stone-600 dark:text-emerald-100/80 mb-4">{t(`${ns}.subtitle`)}</p>

      <div className="grid gap-3 sm:grid-cols-3 mb-4">
        {data.bureaus.map((bureau) => (
          <BureauRow key={bureau.bureau_key} bureau={bureau} t={t} ns={ns} />
        ))}
      </div>

      {attachment ? (
        <InvestigationResultsCard
          attachment={attachment}
          onChange={handleChangeAttachment}
          onRemove={handleRemoveAttachment}
          removing={removing}
          t={t}
          ns={ns}
        />
      ) : (
        <div className="mb-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            className="hidden"
            data-testid="investigation-results-file-input"
            onChange={handleFileInputChange}
          />
          <Button
            variant="outline"
            onClick={handleUploadShortcut}
            disabled={uploading}
            className="border-stone-300 dark:border-emerald-700/40 dark:text-emerald-200 w-full sm:w-auto"
            data-testid="upload-investigation-results-btn"
          >
            <Upload size={16} className="mr-2" />
            {uploading ? t(`${ns}.uploading`) : t(`${ns}.uploadResults`)}
          </Button>
          <button
            type="button"
            onClick={() => {
              replaceModeRef.current = false;
              setShowExistingPicker(true);
            }}
            disabled={uploading}
            className="mt-2 block text-xs text-blue-600 dark:text-blue-400 hover:underline"
            data-testid="choose-investigation-results-link"
          >
            {t(`${ns}.chooseFromDocuments`)}
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        {data.any_bureau_ready && (
          <Button
            onClick={() => navigate('/dispute-wizard/factual/select-items?mode=rounds')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="review-unlock-next-round-btn"
          >
            {t(`${ns}.reviewUnlock`)}
          </Button>
        )}
      </div>

      <p className="text-xs text-stone-500 dark:text-emerald-100/50 mt-3 flex items-start gap-1">
        <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
        {t(`${ns}.manualStartNote`)}
      </p>

      <ExistingDocumentPicker
        attachmentRole="investigation_results"
        isOpen={showExistingPicker}
        onClose={() => {
          setShowExistingPicker(false);
          replaceModeRef.current = false;
        }}
        onSelect={submitExistingDocument}
        onUploadNew={() => {
          setShowExistingPicker(false);
          openPickerFlow();
        }}
      />
    </div>
  );
}
